import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Link,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import moment from "moment";

interface EllipsisTooltipProps {
  text: string | string[];
}

const EllipsisTooltip = ({ text }: EllipsisTooltipProps) => {
  const content = Array.isArray(text) ? text.join(", ") : text || "";

  return (
    <Tooltip title={content} arrow>
      <Typography
        component="span"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontFamily: "DM Sans",
          fontWeight: 500,
          fontStyle: "normal",
          fontSize: "14px",
          lineHeight: "100%",
          letterSpacing: "0px",
          color: "#222222",
          pb: 0.5,
          pr: 0.5,
          maxWidth: "100%",
          display: "inline-block",
          verticalAlign: "bottom",
        }}
      >
        {content}
      </Typography>
    </Tooltip>
  );
};

interface JobCardProps {
  title: string;
  jobId: string | number;
  location: string;
  minExperience: string;
  maxExperience: string;
  preferredQualification: string;
  jobType: string;
  workMode: string;
  jobCategory: string;
  minCtc: number;
  maxCtc: number;
  createdAt?: string;
  posted_date?: string;
  postedDate?: string;
  applicationCount?: number;
  newApplicantsCount?: number;
  applicants?: number;
  status?: string;
  onStatusChange?: (jobId: string | number, status: string) => void;
  companyId?: string | number;
  companyName?: string;
  onEditClick: (jobId: string | number, companyId?: string | number) => void;
  onViewClick: (jobId: string | number) => void;
  showEditInHeader?: boolean;
}

const JobCard = ({
  title,
  jobId,
  location,
  minExperience,
  maxExperience,
  preferredQualification,
  jobType,
  workMode,
  jobCategory,
  minCtc,
  maxCtc,
  createdAt,
  posted_date,
  postedDate,
  applicationCount,
  newApplicantsCount,
  applicants,
  status,
  onStatusChange,
  companyId,
  companyName,
  onEditClick,
  onViewClick,
  showEditInHeader = false,
}: JobCardProps) => {
  const displayDate = postedDate || createdAt || posted_date;
  const postedLabel = displayDate
    ? moment(displayDate).isSame(moment(), "day")
      ? "Today"
      : moment(displayDate).fromNow()
    : "";

  return (
    <Card
      sx={{
        paddingX: "16px",
        paddingY: "16px",
        borderRadius: "8px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
        border: "1px solid #E0E0E0",
      }}
    >
      <CardContent>
        {/* Job Title and Edit */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "DM Sans",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "100%",
                letterSpacing: "0px",
                textTransform: "capitalize",
                color: "#222222",
              }}
            >
              {title}
            </Typography>
            {companyName && (
              <Typography variant="body2" sx={{ color: "#756DE9", fontWeight: "500", mt: 0.5 }}>
                {companyName}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* {showEditInHeader && (
              <Typography
                variant="body2"
                sx={{ color: "#756DE9", cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                onClick={() => onEditClick(jobId, companyId)}
              >
                Edit <Edit sx={{ fontSize: "16px", ml: 0.5 }} />
              </Typography>
            )} */}
            {jobCategory && (
              <Chip
                label={jobCategory}
                sx={{
                  backgroundColor:
                    jobCategory?.toLowerCase() === "internship" ? "#E6F0FE" : "#FFF4E6",
                  color:
                    jobCategory?.toLowerCase() === "internship" ? "#1976D2" : "#FF9800",
                  fontWeight: 500,
                  fontSize: "14px",
                  height: "28px",
                  borderRadius: "16px",
                  px: 1.5,
                }}
              />
            )}
          </Box>
        </Box>
        <Grid container spacing={2} justifyContent="center" sx={{ marginTop: "4px" }}>
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#E6E4FE4F",
                borderRadius: "5px",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  textTransform: "capitalize",
                  color: "#ACACAC",
                }}
              >
                Location
              </Typography>
              <Box sx={{ marginTop: "4px" }}>
                <EllipsisTooltip text={location} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#E6E4FE4F",
                borderRadius: "5px",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  textTransform: "capitalize",
                  color: "#ACACAC",
                }}
              >
                Experience
              </Typography>
              <Box sx={{ marginTop: "4px" }}>
                <EllipsisTooltip text={`${minExperience} - ${maxExperience}`} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#E6E4FE4F",
                borderRadius: "5px",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  textTransform: "capitalize",
                  color: "#ACACAC",
                }}
              >
                Qualification
              </Typography>
              <Box sx={{ marginTop: "4px" }}>
                <EllipsisTooltip text={preferredQualification} />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#E6E4FE4F",
                borderRadius: "5px",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  textTransform: "capitalize",
                  color: "#ACACAC",
                }}
              >
                Job Type
              </Typography>
              <Box sx={{ marginTop: "4px" }}>
                <EllipsisTooltip text={jobType} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#E6E4FE4F",
                borderRadius: "5px",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  textTransform: "capitalize",
                  color: "#ACACAC",
                }}
              >
                Work Mode
              </Typography>
              <Box sx={{ marginTop: "4px" }}>
                <EllipsisTooltip text={workMode || jobType} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                backgroundColor: "#E6E4FE4F",
                borderRadius: "5px",
                padding: "8px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "DM Sans",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  textTransform: "capitalize",
                  color: "#ACACAC",
                }}
              >
                CTC
              </Typography>
              <Box sx={{ marginTop: "4px" }}>
                <EllipsisTooltip text={`${minCtc / 100000}L - ${maxCtc / 100000}L`} />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", marginTop: "10px", alignItems: "center", justifyContent: "flex-end" }}>
          <img src="images/client-website/Group-upload.png" width="15px" height="15px" alt="Upload icon" />
          <Typography
            sx={{
              fontFamily: "DM Sans",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#777777",
              marginLeft: "5px",
            }}
          >
            {postedLabel}
          </Typography>
        </Box>
        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "4px",
          }}
        >
          <Typography sx={{ fontSize: "14px", weight: "400", color: "#000105" }}>
            New Application Received
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ backgroundColor: "#FF8960", padding: "2px 10px", borderRadius: "100px", color: "#fff" }}>
              {newApplicantsCount || 0}
            </Typography>
          </Box>
        </Grid>
        <Grid
          container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "4px",
          }}
        >
          <Typography sx={{ fontSize: "14px", weight: "400", color: "#000105" }}>
            Total Application Received
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ backgroundColor: "#B7B3EB", padding: "2px 10px", borderRadius: "100px", color: "#fff" }}>
              {applicants || applicationCount || 0}
            </Typography>
          </Box>
        </Grid>
        <Divider
          sx={{
            marginTop: "15px",
            marginBottom: "15px",
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
            flexWrap: "nowrap",
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: "14px", weight: "400", color: "#000105", whiteSpace: "nowrap" }}>
            <Link
              sx={{
                textDecoration: "none",
                color: "#756DE9",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                  cursor: "pointer",
                },
              }}
              onClick={() => onViewClick(jobId)}
              target="_blank"
              rel="noopener"
            >
              View Applications
            </Link>
          </Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: "20px",
              backgroundColor: "rgba(102, 102, 102, 0.75)",
              minWidth: "1px",
              alignSelf: "center",
            }}
          />
          <Typography sx={{ fontSize: "14px", weight: "400", color: "#000105", whiteSpace: "nowrap" }}>
            <Link
              sx={{
                textDecoration: "none",
                color: "#756DE9",
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={() => onEditClick(jobId, companyId)}
              target="_blank"
              rel="noopener"
            >
              View / Edit Job Post
            </Link>
          </Typography>
          {onStatusChange && (status || 1) && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  height: "20px",
                  backgroundColor: "rgba(102, 102, 102, 0.75)",
                  minWidth: "1px",
                  alignSelf: "center",
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => onStatusChange(jobId, status === "ACTIVE" || status == "1" ? "INACTIVE" : "ACTIVE")}
                sx={{
                  fontSize: "12px",
                  padding: "4px 8px",
                  minWidth: "auto",
                  textTransform: "none",
                  borderColor: status === "ACTIVE" || status == "1" ? "#f44336" : "#4CAF50",
                  color: status === "ACTIVE" || status == "1" ? "#f44336" : "#4CAF50",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: status === "ACTIVE" || status == "1" ? "#d32f2f" : "#45a049",
                    backgroundColor: status === "ACTIVE" || status == "1" ? "rgba(244, 67, 54, 0.08)" : "rgba(76, 175, 80, 0.08)",
                  },
                }}
              >
                {(status === "ACTIVE" || status == "1") ? "Deactivate" : "Activate"} 
              </Button>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default JobCard;

